import { useEffect, useRef, useState } from 'react';
import { Search, X, ScanLine } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { productsAPI } from '@/services/api';

// Type-ahead product picker (name / SKU / barcode). Scanning a barcode + Enter selects the exact match.
export const ProductSuggestInput = ({ value, onSelect, placeholder = 'Scan barcode or type product name / SKU...', testId = 'product-suggest', autoFocus = false }) => {
  const [text, setText] = useState('');
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => { setText(value ? `${value.name}${value.sku ? ` (${value.sku})` : ''}` : ''); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    const q = text.trim();
    if (!q || value) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await productsAPI.search(q, 10);
        const list = res.data?.products || res.data?.items || res.data || [];
        setMatches(Array.isArray(list) ? list : []);
        setOpen(true);
        setActive(0);
      } catch { setMatches([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [text, value]);

  const choose = (p) => { onSelect(p); setOpen(false); };
  const clear = () => { setText(''); onSelect(null); setMatches([]); };

  const onKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const q = text.trim();
      if (!q) return;
      try {
        const res = await productsAPI.getByBarcode(q);
        if (res.data?.id) { choose(res.data); return; }
      } catch { /* not a barcode; fall back to the highlighted suggestion */ }
      if (matches[active]) choose(matches[active]);
    } else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <Input value={text} placeholder={placeholder} autoFocus={autoFocus} autoComplete="off"
        onChange={(e) => { setText(e.target.value); if (value) onSelect(null); }}
        onFocus={() => matches.length && setOpen(true)} onKeyDown={onKeyDown}
        className="pl-10 pr-8 bg-white border-gray-300" data-testid={`${testId}-input`} />
      {text && (
        <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid={`${testId}-clear`} aria-label="Clear product">
          <X className="w-4 h-4" />
        </button>
      )}
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto text-sm" data-testid={`${testId}-list`}>
          {matches.map((p, i) => (
            <li key={p.id} onMouseDown={(e) => { e.preventDefault(); choose(p); }} onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${i === active ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
              data-testid={`${testId}-option-${p.sku || p.id}`}>
              <span className="text-gray-800 truncate">{p.name}</span>
              <span className="text-xs text-gray-500 font-mono shrink-0">{p.sku}{p.barcode ? ` · ${p.barcode}` : ''}</span>
            </li>
          ))}
        </ul>
      )}
      {open && text.trim() && !value && matches.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500 flex items-center gap-2"><Search className="w-3.5 h-3.5" /> No product matches</div>
      )}
    </div>
  );
};
